import { FC, useState } from 'react'
import { Item as TItem } from '../types'
import { twMerge } from '@lib/tailwind-merge'
import { ItemPath } from './ItemPath'
import { IconButton } from 'components/buttons'
import { faSquareMinus } from '@fortawesome/pro-solid-svg-icons'
import {
  faChevronDown,
  faChevronUp,
  faSquarePlus,
} from '@fortawesome/pro-regular-svg-icons'
import { ActiveDiamond } from './ActiveDiamond'
import { Tooltip } from '@lib/tooltip'
import { useTranslation } from 'react-i18next'
import {
  isClickupTaskSourceDetails,
  isJiraTaskSourceDetails,
  isShotgridTaskSourceDetails,
} from 'utils/sourceDetailsTypeGuards'
import {
  Row,
  TwoColumnTinyTable,
} from 'components/tables/two-column-tiny-table'

export interface Props {
  className?: string
  item: TItem
  onItemClick: (id: number, checked: boolean) => void
  path?: string[]
}

export const SkeletonItem = () => (
  <div className="h-11 grow my-2 mx-9 bg-neutral-100 animate-pulse"></div>
)

export const Item: FC<Props> = ({ className, item, onItemClick, path }) => {
  const { t } = useTranslation()
  const [expanded, setExpaned] = useState(false)

  return (
    <div className={twMerge('flex flex-col', className)}>
      {path && <ItemPath path={path} />}
      <div
        className={twMerge(
          'flex flex-col p-1 my-1 text-sm rounded bg-neutral-150',
          item.checked && 'bg-primary-100',
        )}
      >
        <div className={twMerge('flex flex-row items-center')}>
          <label
            data-tooltip-id={`tooltip-${item.task.id}`}
            key={item.task.id}
            className={twMerge(
              'flex flex-grow flex-row items-center text-sm font-semibold cursor-pointer select-none',
              item.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <IconButton
              id={`item-${item.task.id}`}
              className={twMerge(
                'hover:bg-primary-200 group w-8 h-8 text-neutral-500 mr-2',
                item.checked && 'text-primary-600',
              )}
              disabled={item.disabled}
              icon={item.checked ? faSquareMinus : faSquarePlus}
              iconProps={{
                className: 'group-hover:text-primary-800',
                size: 'lg',
              }}
              onClick={() => onItemClick(item.task.id, !item.checked)}
              circle={true}
            />
            {item.task.name}
          </label>
          {item.active !== undefined && (
            <ActiveDiamond active={item.active} className="mr-1" />
          )}
          <IconButton
            id={`item-${item.task.id}`}
            className="w-8 h-8 text-neutral-600 ml-auto hover:bg-neutral-300"
            icon={expanded ? faChevronUp : faChevronDown}
            onClick={() => setExpaned((expanded) => !expanded)}
            srOnlyLabel={t(
              expanded
                ? 'features.timeLogging.taskSelection.collapse'
                : 'features.timeLogging.taskSelection.expand',
            )}
          />
        </div>
        {expanded && (
          <TwoColumnTinyTable>
            {isShotgridTaskSourceDetails(item.task.sourceDetails) && (
              <Row
                label={t('common.shotgridId')}
                value={item.task.sourceDetails.shotgridId}
              />
              // TODO: SG/Flow PT status
            )}
            {isJiraTaskSourceDetails(item.task.sourceDetails) && (
              <>
                <Row
                  label={t('common.jiraId')}
                  value={item.task.sourceDetails.jiraId}
                />
                <Row
                  label={t('common.summary')}
                  value={item.task.sourceDetails.summary}
                />
              </>
            )}
            {isClickupTaskSourceDetails(item.task.sourceDetails) && (
              <Row
                label={t('common.clickupId')}
                value={item.task.sourceDetails.clickupId}
              />
            )}
            <Row label={t('common.timeLoggerId')} value={item.task.id} />
          </TwoColumnTinyTable>
        )}
      </div>
      {item.disabled && (
        <Tooltip
          id={`tooltip-${item.task.id}`}
          place="bottom"
          content={t('common.removeTaskDisabledTooltip')}
        />
      )}
    </div>
  )
}
